import React, { ReactElement, useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import classnames from 'classnames';
import { Stack, ScrollArea, Group, Button } from '@mantine/core';
import ConfirmRemoveModal from './ConfirmRemoveModal';
import { IGetOrgJobsAPIParams, IOrgJobsPanelProps, INode, ISelectedMatch, IMatchDataLoading } from '../Types/BulkPricingTypes';
import { getOrgJobsData } from '../BulkPricingAPIs';
import { OrgJobCard } from '../OrgJobs/OrgJobCard';
import { IAppState } from '../../../../lib/types/AppState/index';
import ViewSelector from '../../../ViewSelector/ViewSelector';
import BulkPricingPanelLoading from '../BulkPricingPanel/BulkPricingPanelLoading';
import { CATEGORY_CODE, PAGE_CODE, PAGE } from '../../../../lib/pageCodeDefaults';
import { elementShortNames } from '../../../../lib/constants';
import { formatOrgJob } from '../utils';
import { addSelectedJobs, clearAllSelections } from '../../LandingPage/LandingPageActions';
import addAlert from 'ps-components/AlertToast/addAlert';

import './OrgJobsPanel.scss';

export const OrgJobsPanel: React.FC<IOrgJobsPanelProps> = (props): ReactElement => {
  const selectedJobs = useSelector((state: IAppState) => state.landingPage.landingPageReducer.selectedJobs);
  const masterAccountID = useSelector((state: IAppState) => state.session.organization.masterAccountId);
  const benchmarkYear = useSelector((state: IAppState) => state.session.benchmarkYear);
  const dispatch = useDispatch();
  const history = useHistory();

  const [selectedMatches, setSelectedMatches] = useState<ISelectedMatch[]>([]);
  const [orgJobsStale, setOrgJobsStale] = useState(true);
  const [confirmRemoveModalOpened, setConfirmRemoveModalOpened] = useState(false);
  const [messages, setMessages] = useState<{ text: string | string[], type: string }[]>([]);
  const [hasDisplayedMessages, setHasDisplayedMessages] = useState(false);
  const [matchDataLoading, setMatchDataLoading] = useState<IMatchDataLoading>({});

  const selectedJobsFromStorage = window.localStorage.getItem('selectedJobs');
  const { checkedDataCuts, setCheckedDataCuts, orgJobs, setOrgJobs, orgJobsLoading, setOrgJobsLoading } = props;

  const APIParams: IGetOrgJobsAPIParams = {
    jobs: selectedJobs,
    masterAccountID
  };

  useEffect(() => {
    if (selectedJobsFromStorage === null && !selectedJobs.length) {
      history.push('/market-analysis/market-pricing', {
        notFirstLoad: true,
        searchQuery: history.location?.state?.searchQuery,
        offset: 0,
        bulkPricing: false
      });
    };

    if (selectedJobsFromStorage !== null && !selectedJobs.length) {
      APIParams.jobs = JSON.parse(selectedJobsFromStorage);
      dispatch(clearAllSelections());
      dispatch(addSelectedJobs(APIParams.jobs));
    };
    if (orgJobsStale && selectedJobs.length) {
      setOrgJobsLoading(true);
      const promisedData = getOrgJobsData(APIParams);
      formatAndSetOrgJobsData(promisedData);
      if (selectedJobs.length) {
        window.localStorage.setItem('selectedJobs', JSON.stringify(selectedJobs));
      };
    }
  }, [orgJobsStale, selectedJobs]);

  useEffect(() => {
    if (messages.length && !hasDisplayedMessages) {
      const successMessages = messages.filter((message) => message.type === 'success');
      const failureMessages = messages.filter((message) => message.type !== 'success');
      successMessages.forEach((message) => {
        addAlert(message.text, message.type);
      });
      failureMessages.forEach((message) => {
        addAlert(message.text, message.type, 'manual');
      });
      setHasDisplayedMessages(true);
    };
  }, [messages, hasDisplayedMessages]);

  useEffect (() => {
    if (orgJobs.length) {
      setOrgJobsLoading(false);
    };
  }, [orgJobs]);

  const viewSelectorOnChange = () => {
    setOrgJobsStale(true);
  }

  const formatAndSetOrgJobsData = async (promisedData: Promise<any>) => {
    const orgJobsData = await promisedData;
    let formattedResults: INode[] = orgJobsData.data.map((job: any[]) => formatOrgJob(job, orgJobsData.terms))

    if (selectedJobs.length && formattedResults.length !== selectedJobs.length) {
      history.push('/market-analysis/market-pricing', {
        notFirstLoad: true,
        searchQuery: history.location?.state?.searchQuery,
        offset: 0,
        bulkPricing: false
      });
      dispatch(clearAllSelections());
      window.localStorage.removeItem('selectedJobs');
      return;
    }
    setOrgJobs([...formattedResults]);
    setOrgJobsStale(false);
  }

  const updateMatchesInState = (updatedJob: INode) => {
    setOrgJobs((pending) => {
      const updatedOrgJobs = pending.map((orgJob) => {
        if (orgJob.id === updatedJob.id) {
          return { ...updatedJob };
        }
        return orgJob;
      });
      return updatedOrgJobs;
    });
  };

  const handleRemoveSelected = () => {
    setConfirmRemoveModalOpened(true);
  };

  return (
    <ScrollArea offsetScrollbars className={classnames('org-jobs__panel', 'bulk-pricing__panel')}>
      <Stack gap='lg'>
        <Group justify="flex-start">
          <ViewSelector
            label='Job View'
            pageName='Job Fields'
            pageCode={PAGE_CODE.BULK_PRICING_ORG_JOBS}
            categoryCode={CATEGORY_CODE.BULK_PRICING_ORG_JOBS}
            page={PAGE.BULK_PRICING_ORG_JOBS}
            elementCountLimit={3}
            showAggregates
            includeAggregates
            elementsFilter={(element: any): boolean => {
              if (element.field_type_code !== 'monetary' && !element?.text?.startsWith('Compa Ratio')) return false;
            
              const elementName = Object.values(elementShortNames[PAGE_CODE.BULK_PRICING_ORG_JOBS])
                .find(({ stem }) => {
                    return element?.text?.startsWith(stem)
                })
              return Boolean(elementName);
            }}
            categoryFilter={(category: any): boolean => (category?.text !== 'User Defined Formulas')}
            onChange={viewSelectorOnChange}
            hideRequiredFields={true}
          />
          <ViewSelector
            label='Pricing View'
            pageName='Pricing Fields'
            pageCode={PAGE_CODE.BULK_PRICING_PRICE}
            categoryCode={CATEGORY_CODE.BULK_PRICING_PRICE}
            page={PAGE.BULK_PRICING_PRICE}
            elementCountLimit={5}
            categoryFilter={(category: any): boolean => (category?.text !== 'User Defined Formulas')}
            onChange={viewSelectorOnChange}
            dropdownClass='pricing-view'
          />
        </Group>
        <Group justify='space-between' h={40}>
          <Button
            className='remove-selected-link'
            variant="transparent"
            disabled={selectedMatches.length === 0}
            onClick={() => setSelectedMatches([])}
            color="skyblue"
          >
            Clear All Selections
          </Button>
          <Button 
              className='remove-selected-link' 
              variant="transparent"
              disabled={selectedMatches.length === 0}
              onClick={handleRemoveSelected}
              color="skyblue"
            >
              Remove Selected Matches
          </Button>
        </Group>
        {orgJobsLoading ? 
          <BulkPricingPanelLoading /> : 
          orgJobs.map((job) => {
            return <OrgJobCard 
              key={job.id} 
              job={job} 
              checkedDataCuts={checkedDataCuts}
              updateMatchesInState={updateMatchesInState}
              selectedMatches={selectedMatches}
              setSelectedMatches={setSelectedMatches}
              setOrgJobsDataStale={setOrgJobsStale}
              setMessages={setMessages}
              setHasDisplayedMessages={setHasDisplayedMessages}
              matchDataLoading={matchDataLoading}
              setMatchDataLoading={setMatchDataLoading}
              setCheckedDataCuts={setCheckedDataCuts}
            />
          })
        }
      </Stack>
      <ConfirmRemoveModal
        opened={confirmRemoveModalOpened}
        onClose={() => setConfirmRemoveModalOpened(false)}
        setSelectedMatches={setSelectedMatches}
        selectedMatches={selectedMatches}
        benchmarkYear={benchmarkYear}
        setMatchDataLoading={setMatchDataLoading}
        updateMatchesInState={updateMatchesInState}
        masterAccountID={masterAccountID}
        orgJobs={orgJobs}
      />
    </ScrollArea>
  );
};
